import { t } from "@travily/i18n";
import type Badge from "@travily/types/models/BadgesRow";
import { HoverCard, type HoverCardProps } from "@ui/components/HoverCard";
import { formatDate } from "@ui/lib/format-date";
import type { ReactNode } from "react";
import { RarityStamp } from "./RarityStamp";

interface BadgeHoverCardProps {
  badge: Badge;
  children: ReactNode;
  placement?: HoverCardProps["placement"];
  triggerClassName?: string;
}

// award_count over the profile count can land below 1% for common badges (and an
// async-backfilled rarity can briefly arrive as 0); show "<1%" for anything under 1
// rather than "0%"/"0.5%" so the copy always reads as a real share.
function formatRarity(percent: number): string {
  if (percent < 1) return "<1";
  return String(Math.round(percent));
}

/**
 * Tooltip/hovercard for a single badge: the name set in the passport display face, a
 * short description and the date earned on the left, with the badge's rarity stamped
 * as a passport-style entry mark on the right (see RarityStamp). A caret ties it to
 * the sticker. Hovers on pointer devices, opens on tap on touch devices (HoverCard
 * handles the pointer-type gating). Fields are null-guarded so a profile rendered
 * with pre-deploy props (missing the new badge fields) still renders cleanly instead
 * of "undefined".
 */
export function BadgeHoverCard({ badge, children, placement, triggerClassName }: BadgeHoverCardProps) {
  return (
    <HoverCard
      placement={placement}
      triggerClassName={triggerClassName}
      showArrow
      // Tighter padding + radius than the default tooltip, sized to its content
      // rather than a fixed width (kills the dead space).
      className="max-w-[16rem] rounded-[16px] p-3"
      content={
        <div className="flex items-start gap-3 text-left">
          <div className="flex min-w-0 max-w-[9.5rem] flex-col gap-1">
            <p className="font-passport font-semibold text-[15px] text-neutral-900 leading-tight">{badge.label}</p>
            {badge.description && <p className="text-[13px] text-neutral-600 leading-snug">{badge.description}</p>}
            {badge.awarded_at && (
              <p className="mt-0.5 text-[11px] text-neutral-400">
                {t("passport.badges.hovercard.earned_on", { date: formatDate(badge.awarded_at) })}
              </p>
            )}
          </div>
          {typeof badge.rarity_percent === "number" && (
            <div className="shrink-0">
              <RarityStamp
                value={`${formatRarity(badge.rarity_percent)}%`}
                topLabel={t("passport.badges.hovercard.rarity_prefix")}
                bottomLabel={t("passport.badges.hovercard.rarity_suffix")}
                className="size-16"
              />
              <span className="sr-only">
                {t("passport.badges.hovercard.rarity", { percent: formatRarity(badge.rarity_percent) })}
              </span>
            </div>
          )}
        </div>
      }
    >
      {children}
    </HoverCard>
  );
}
